import React, { useState, useEffect } from 'react'

interface AdminContact {
  id: number
  contactType: string
  email: string
  name?: string
  isActive: boolean
  isPrimary: boolean
  createdAt: string
  updatedAt: string
}

interface NotificationSetting {
  id: number
  settingKey: string
  settingValue: string
  description?: string
}

const AdminContactsManager: React.FC = () => {
  const [contacts, setContacts] = useState<AdminContact[]>([])
  const [settings, setSettings] = useState<NotificationSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'contacts' | 'settings'>('contacts')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingContact, setEditingContact] = useState<AdminContact | null>(null)

  // Form states
  const [formData, setFormData] = useState({
    contactType: 'iso_approvals',
    email: '',
    name: '',
    isActive: true,
    isPrimary: false
  })

  const contactTypes = [
    { value: 'iso_approvals', label: 'ISO Approvals', icon: '👥' },
    { value: 'system_alerts', label: 'System Alerts', icon: '⚠️' },
    { value: 'support', label: 'Support Requests', icon: '🎧' },
    { value: 'general', label: 'General Notifications', icon: '📧' }
  ]

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError('')

      const [contactsResponse, settingsResponse] = await Promise.all([
        fetch('/api/admin/contacts', { credentials: 'include' }),
        fetch('/api/admin/contacts/settings', { credentials: 'include' })
      ])

      if (contactsResponse.ok) {
        const contactsData = await contactsResponse.json()
        setContacts(contactsData.contacts || [])
      } else {
        throw new Error('Failed to fetch contacts')
      }

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json()
        setSettings(settingsData.settings || [])
      } else {
        throw new Error('Failed to fetch settings')
      }

    } catch (error: any) {
      setError(error.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      const url = editingContact 
        ? `/api/admin/contacts/${editingContact.id}`
        : '/api/admin/contacts'
      
      const method = editingContact ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        await fetchData()
        resetForm()
        setError('')
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to save contact')
      }
    } catch (error: any) {
      setError(error.message || 'Network error')
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this contact?')) {
      return
    }

    try {
      const response = await fetch(`/api/admin/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (response.ok) {
        await fetchData()
        setError('')
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to delete contact')
      }
    } catch (error: any) {
      setError(error.message || 'Network error')
    }
  }

  const handleEdit = (contact: AdminContact) => {
    setEditingContact(contact)
    setFormData({
      contactType: contact.contactType,
      email: contact.email,
      name: contact.name || '',
      isActive: contact.isActive,
      isPrimary: contact.isPrimary
    })
    setShowAddForm(true)
  }

  const resetForm = () => {
    setFormData({
      contactType: 'iso_approvals',
      email: '',
      name: '',
      isActive: true,
      isPrimary: false
    })
    setEditingContact(null)
    setShowAddForm(false)
  }

  const updateSetting = async (key: string, value: string) => {
    try {
      const response = await fetch(`/api/admin/contacts/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ value })
      })

      if (response.ok) {
        await fetchData()
        setError('')
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to update setting')
      }
    } catch (error: any) {
      setError(error.message || 'Network error')
    }
  }

  const getContactTypeInfo = (type: string) => {
    return contactTypes.find(ct => ct.value === type) || { value: type, label: type, icon: '📧' }
  }

  const groupedContacts = contactTypes.map(type => ({
    ...type,
    contacts: contacts.filter(c => c.contactType === type.value)
  }))

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading contact management...</p>
      </div>
    )
  }

  return (
    <div>
      {/* Page Header */}
      <div className="admin-content-header">
        <h2 className="admin-page-title">Notification Settings</h2>
        <div className="btn-group" role="group">
          <button
            className={`btn ${activeTab === 'contacts' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('contacts')}
          >
            <i className="bi bi-person-lines-fill me-1"></i>
            Contacts
          </button>
          <button
            className={`btn ${activeTab === 'settings' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="bi bi-gear me-1"></i>
            Settings
          </button>
        </div>
      </div>

      {/* Page Description */}
      <div className="px-4 py-3 bg-light border-bottom">
        <p className="mb-0 text-muted">
          Configure admin notification contacts and system-wide email settings. Manage who receives notifications for different types of system events.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger alert-dismissible mx-4 mt-3" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div>
          {/* Section Header */}
          <div className="card mb-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">Contact Configuration</h6>
                  <p className="text-muted mb-0 small">
                    Manage admin contact emails for different notification types
                  </p>
                </div>
                <button
                  className="btn btn-success"
                  onClick={() => setShowAddForm(true)}
                >
                  <i className="bi bi-plus-circle me-1"></i>
                  Add Contact
                </button>
              </div>
            </div>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="card mt-3">
              <div className="card-header">
                <h6 className="mb-0">
                  {editingContact ? 'Edit Contact' : 'Add New Contact'}
                </h6>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Contact Type</label>
                      <select
                        className="form-select"
                        value={formData.contactType}
                        onChange={(e) => setFormData({...formData, contactType: e.target.value})}
                        required
                      >
                        {contactTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Name (Optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Options</label>
                      <div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                          />
                          <label className="form-check-label">Active</label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={formData.isPrimary}
                            onChange={(e) => setFormData({...formData, isPrimary: e.target.checked})}
                          />
                          <label className="form-check-label">Primary</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button type="submit" className="btn btn-primary me-2">
                      {editingContact ? 'Update Contact' : 'Add Contact'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={resetForm}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Contacts by Type */}
          {groupedContacts.map(group => (
            <div key={group.value} className="card mt-3">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    {group.icon} {group.label}
                  </h6>
                  <span className="badge bg-secondary">
                    {group.contacts.length} contact(s)
                  </span>
                </div>
              </div>
              <div className="card-body p-0">
                {group.contacts.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No contacts configured for this type</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Primary</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.contacts.map(contact => (
                          <tr key={contact.id}>
                            <td>{contact.email}</td>
                            <td>{contact.name || '-'}</td>
                            <td>
                              <span className={`badge ${contact.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                {contact.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {contact.isPrimary && (
                                <span className="badge bg-info">Primary</span>
                              )}
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => handleEdit(contact)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-outline-danger"
                                  onClick={() => handleDelete(contact.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'settings' && (
        <div>
          {/* Section Header */}
          <div className="card mb-0">
            <div className="card-body">
              <h6 className="mb-1">System Settings</h6>
              <p className="text-muted mb-0 small">
                Configure global notification settings and preferences
              </p>
            </div>
          </div>
          
          <div className="card mt-3">
            <div className="card-header">
              <h6 className="mb-0">Notification Settings</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Setting</th>
                      <th>Current Value</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map(setting => (
                      <tr key={setting.id}>
                        <td>
                          <code>{setting.settingKey}</code>
                        </td>
                        <td>
                          {setting.settingValue === 'true' || setting.settingValue === 'false' ? (
                            <span className={`badge ${setting.settingValue === 'true' ? 'bg-success' : 'bg-secondary'}`}>
                              {setting.settingValue === 'true' ? 'Enabled' : 'Disabled'}
                            </span>
                          ) : (
                            <span className="badge bg-info">{setting.settingValue}</span>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">
                            {setting.description || 'No description available'}
                          </small>
                        </td>
                        <td>
                          {(setting.settingValue === 'true' || setting.settingValue === 'false') && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => updateSetting(
                                setting.settingKey, 
                                setting.settingValue === 'true' ? 'false' : 'true'
                              )}
                            >
                              Toggle
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminContactsManager